import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { guidFor } from '@ember/object/internals';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Select from 'tio-ui/components/select';
import Text from 'tio-ui/components/text';
import ContactSupportHint from 'tio-employee/components/slr/resolution-alert/contact-support-hint';
import {
  rankServicerOptions,
  type ResolutionAlert,
  type ServicerOption,
} from 'tio-employee/utils/slr/resolution-alert';
import type MemberResolutionsService from 'tio-employee/services/member-resolutions';

interface ServicerAnswerFormSignature {
  Args: {
    /** The open servicer question; its suggested servicers are pinned above the catalog. */
    alert: ResolutionAlert;
    /** Prefixes the controls' test hooks so each mounting surface stays distinct. */
    testHookPrefix: string;
    /** True while the host's answer request is in flight; the picker and Confirm lock until it settles. */
    isSubmitting: boolean;
    /** Called with the picked servicer; the host owns the request and its outcome. */
    onConfirm: (servicer: ServicerOption) => void;
  };
}

/**
 * The servicer question's answer control: every supported servicer with the question's
 * suggestions pinned first, and "Confirm Servicer", disabled until a pick.
 */
export default class ServicerAnswerForm extends Component<ServicerAnswerFormSignature> {
  @service('member-resolutions') declare memberResolutions: MemberResolutionsService;

  @tracked selectedId: string | null = null;

  selectId = `servicer-answer-${guidFor(this)}`;

  get isCatalogLoading() {
    return this.memberResolutions.servicerCatalog.isLoading;
  }

  get options() {
    return rankServicerOptions(
      this.args.alert.suggestedServicers,
      this.memberResolutions.servicerCatalog.value ?? []
    );
  }

  get items() {
    return this.options.map((servicer) => ({ key: servicer.id, label: servicer.name }));
  }

  get isCatalogUnavailable() {
    return (
      !this.isCatalogLoading &&
      (this.memberResolutions.servicerCatalog.isError || this.options.length === 0)
    );
  }

  get selectedServicer() {
    return this.options.find((servicer) => servicer.id === this.selectedId);
  }

  get isConfirmDisabled() {
    return !this.selectedServicer || this.args.isSubmitting;
  }

  select = (key: string) => {
    this.selectedId = key || null;
  };

  confirm = () => {
    if (this.selectedServicer) this.args.onConfirm(this.selectedServicer);
  };

  <template>
    <div class="tio-row flex-wrap items-end w-full" data-test-servicer-answer-form>
      <div class="tio-col-extra-tight tio-fill-min min-w-56">
        <div class="tio-row-extra-tight">
          <label
            class="tio-text-xs-semibold uppercase tracking-wide tio-fg-secondary"
            for={{this.selectId}}
          >
            {{t "slr.reimagined.resolution_alert.dropdown.label"}}
          </label>
          <ContactSupportHint
            @copyKey="slr.reimagined.resolution_alert.dropdown"
            @testHook="{{@testHookPrefix}}__contact-support"
          />
        </div>
        <Select
          @id={{this.selectId}}
          @items={{this.items}}
          @selected={{this.selectedId}}
          @onChange={{this.select}}
          @placeholder={{t "slr.reimagined.resolution_alert.dropdown.placeholder"}}
          @isLoading={{this.isCatalogLoading}}
          @clearable={{false}}
          @disabled={{@isSubmitting}}
          @testHook="{{@testHookPrefix}}__servicer"
          data-test-servicer-answer-select
        />
      </div>
      <Button
        @intent="primary"
        @isDisabled={{this.isConfirmDisabled}}
        @isRunning={{@isSubmitting}}
        @testHook="{{@testHookPrefix}}__confirm"
        data-test-servicer-answer-confirm
        {{on "click" this.confirm}}
      >
        {{t "slr.reimagined.resolution_alert.dropdown.confirm"}}
      </Button>
    </div>
    {{#if this.isCatalogUnavailable}}
      <Text @size="sm" @variant="muted" data-test-servicer-answer-unavailable>
        {{t "slr.reimagined.resolution_alert.dropdown.catalog_unavailable"}}
      </Text>
    {{/if}}
  </template>
}
